<script lang="ts" setup>
import { adminFortnoxUsersService } from '@/services/admin/admin-fortnox-users-service';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import {
  PermissionType,
  ReviewPermissionType,
  UserRole,
} from 'cesg-shared/dist/models';
import { computed, onMounted, ref } from 'vue';
import UsersManagmentView, {
  type UsersManagmentAction,
  type UsersManagmentViewProps,
} from './UsersManagmentView.vue';

type ModalUser = UsersManagmentViewProps['data']['selectedUser'];

type FetchedData = {
  users: Awaited<
    ReturnType<typeof adminFortnoxUsersService.getUsersAndProjects>
  >;
};

const fetchedData = ref<FetchedData>({
  users: [],
});

const allRoles = Object.values(UserRole);
const selectedRoles = ref<UserRole[]>([...allRoles]);

const toggleRole = (role: UserRole) => {
  if (selectedRoles.value.includes(role)) {
    if (selectedRoles.value.length === 1) return;
    selectedRoles.value = selectedRoles.value.filter((r) => r !== role);
  } else {
    selectedRoles.value = [...selectedRoles.value, role];
  }
};

const toViewFormat = (
  fetchedData: FetchedData,
): UsersManagmentViewProps['data'] => {
  return {
    allRoles,
    selectedRoles: selectedRoles.value,
    users: fetchedData.users
      .filter(({ user }) => selectedRoles.value.includes(user.role as UserRole))
      .map(({ projects, user }) => ({
        ...user,
        projects: projects.map((project) => project.name).join(', '),
      })),
    selectedUser: selectedUser.value,
    availableRoles: Array.from(Object.values(UserRole)),
  };
};
const usersManagmentViewProps = computed(() => toViewFormat(fetchedData.value));

const selectedUser = ref<ModalUser>(null);

const openUserModal = (userId: string) => {
  const toModalProjects = (
    projects: FetchedData['users'][number]['projects'],
    permissions: FetchedData['users'][number]['permissions'],
  ): NonNullable<ModalUser>['projects'] => {
    return projects.map((project) => {
      const { overview, budget, forecast, ata, invoice, files, review } =
        permissions.find((perm) => perm.projectId === project.id) ?? {
          overview: PermissionType.DISABLED,
          budget: PermissionType.DISABLED,
          forecast: PermissionType.DISABLED,
          ata: PermissionType.DISABLED,
          invoice: PermissionType.DISABLED,
          files: PermissionType.DISABLED,
          review: ReviewPermissionType.PROHIBITED,
        };
      return {
        id: project.id,
        name: project.name,
        number: project.fortnoxId,
        status: project.status ?? undefined,
        permissions: {
          overview,
          budget,
          forecast,
          ata,
          invoice,
          files,
          review,
        },
      };
    });
  };

  const rowUser = fetchedData.value.users.find(
    ({ user }) => user.id === userId,
  );

  if (!rowUser) {
    return;
  }

  selectedUser.value = {
    id: rowUser.user.id,
    email: rowUser.user.email,
    name: rowUser.user.name,
    phone: rowUser.user.phone ?? '',
    role: rowUser.user.role,
    projects: toModalProjects(rowUser.projects, rowUser.permissions),
  };
};

const onModalAction = (
  action: Extract<UsersManagmentAction, { type: 'modalAction' }>['action'],
) => {
  if (
    action.type === 'close' ||
    action.type === 'cancel' ||
    action.type === 'save'
  ) {
    selectedUser.value = null;
  }

  if (action.type === 'save') {
    loadData();
  }
};

const onAction = (payload: UsersManagmentAction) => {
  switch (payload.type) {
    case 'rowClick':
      openUserModal(payload.userId);
      break;
    case 'toggleRole':
      toggleRole(payload.role);
      break;
    case 'modalAction':
      onModalAction(payload.action);
      break;
  }
};

const loadData = async () => {
  try {
    overlaySpinnerService.show();

    const [users] = await Promise.all([
      adminFortnoxUsersService.getUsersAndProjects(),
    ]);

    fetchedData.value = {
      users,
    };
  } finally {
    overlaySpinnerService.hide();
  }
};

onMounted(loadData);
</script>

<template>
  <UsersManagmentView :data="usersManagmentViewProps" @action="onAction" />
</template>
